import { guff } from 'bam-node-baseball-savant/utils/guff'

import {
    isGamefeedRawData,
    type EVPData,
    type IllustratorRawData,
} from '../schema/types.ts'

function threeDigitFormat(
    val: string | null | undefined | number,
    placeholder = null
): string | null {
    try {
        if (val === null || val === undefined) {
            return placeholder
        }

        const num = Number(val)

        if (num >= 0 && num < 1) {
            return num.toFixed(3).slice(-4)
        }
        // Negative numbers? Meh
        return num.toFixed(3)
    } catch (e) {
        console.error('threeDigitFormat error', e, val)

        return String(val)
    }
}

const batTrackingMetricKeys: Record<
    string,
    { dbProp: keyof IllustratorRawData }
> = {
    attackAngle: {
        dbProp: 'attack_angle',
    },
    attackDirection: {
        dbProp: 'attack_direction',
    },
    batSpeed: {
        dbProp: 'sweetspot_speed_mph',
    },
    swingLength: {
        dbProp: 'swing_length',
    },
    swingPathTilt: {
        dbProp: 'swing_path_tilt',
    },
    idealAttackAngle: {
        // This isn't a db property, but I add it manually
        dbProp: 'ideal_angle',
    },
}

export type Calculations = {
    totalPitches: number
    hits: number
    bip: number
    trackedBIP: number
    trackedSwingLength: number
    trackedBatSpeed: number
    trackedSwingLengthCompetitive: number
    trackedBatSpeedCompetitive: number
    ab: number
    pa: number
    hr: number
    so: number
    single: number
    double: number
    triple: number
    pitches: number
    wobaNumer: number
    wobaDenom: number
    xWobaNumer: number
    xWobaDenom: number
    runExp: number | string
    pitcherRunExp: number | string
    swings: number
    whiffs: number
    launchSpeed: number
    launchAngle: number
    releaseSpeedSum: number
    releaseSpinRateSum: number
    batSpeedSum: number
    batSpeedCompetitiveSum: number
    swingLengthSum: number
    swingLengthCompetitiveSum: number
    idealAttackAngleSum: number
    idealAttackAngleCompetitiveSum: number
    attackAngleSum: number
    attackAngleCompetitiveSum: number
    attackDirectionSum: number
    attackDirectionCompetitiveSum: number
    swingPathTiltSum: number
    swingPathTiltCompetitiveSum: number
    percent: string | null
    avgExitVelocity: string | null
    avgLaunchAngle: string | null
    ba: string | null
    woba: string | null
    slg: string | null
    slgRaw: number | null
    xWoba: string | null
    kPercent: string | null
    swing_miss_percent: string | null
    releaseSpeed: string | number | null
    releaseSpinRate: number | null
    batSpeed: number | null
    batSpeedCompetitive: number | null
    swingLength: number | null
    swingLengthCompetitive: number | null
    idealAttackAngle: number | null
    idealAttackAngleCompetitive: number | null
    attackAngle: number | null
    attackAngleCompetitive: number | null
    attackDirection: number | null
    attackDirectionCompetitive: number | null
    swingPathTilt: number | null
    swingPathTiltCompetitive: number | null
}

type BatTrackingMetricKey = keyof typeof batTrackingMetricKeys

const batTrackingMetrics = Object.keys(
    batTrackingMetricKeys
) as BatTrackingMetricKey[]

function addBatTrackingMetricsRaw(calculations: Calculations, row: EVPData) {
    if (isGamefeedRawData(row.raw)) {
        return
    }

    const rawData = row.raw

    batTrackingMetrics.forEach((prop: BatTrackingMetricKey) => {
        const { dbProp } = batTrackingMetricKeys[prop]

        if (!dbProp) {
            throw new Error(`missing database property ${prop}`)
        }

        const sumKey = `${prop}Sum` as keyof Calculations
        const competitiveSumKey = `${prop}CompetitiveSum` as keyof Calculations

        // eslint-disable-next-line no-param-reassign
        ;(calculations[sumKey] as number) += row.trackedBatSpeed
            ? Number(rawData[dbProp])
            : 0
        // eslint-disable-next-line no-param-reassign
        ;(calculations[competitiveSumKey] as number) +=
            row.trackedBatSpeedCompetitive ? Number(rawData[dbProp]) : 0
    })
}

function addBatTrackingMetricsPercents(
    calculations: Calculations,
    trackedBatSpeed: number,
    trackedBatSpeedCompetitive: number
) {
    batTrackingMetrics.forEach((prop) => {
        const sumKey = `${prop}Sum` as keyof Calculations
        const competitiveKey = `${prop}Competitive` as keyof Calculations
        const competitiveSumKey = `${prop}CompetitiveSum` as keyof Calculations

        // eslint-disable-next-line no-param-reassign
        ;(calculations[sumKey] as number | null) = trackedBatSpeed
            ? (calculations[sumKey] as number) / trackedBatSpeed
            : null

        // eslint-disable-next-line no-param-reassign
        ;(calculations[competitiveKey] as number | null) =
            trackedBatSpeedCompetitive
                ? (calculations[competitiveSumKey] as number) /
                  trackedBatSpeedCompetitive
                : null
    })
}

export default function runCalculations(
    data: EVPData[],
    totalSize: number
): Calculations {
    const trackedSwingLength = data.filter((d) => d.trackedSwingLength).length
    const trackedSwingLengthCompetitive = data.filter(
        (d) => d.trackedSwingLengthCompetitive
    ).length
    const trackedBatSpeed = data.filter((d) => d.trackedBatSpeed).length
    const trackedBatSpeedCompetitive = data.filter(
        (d) => d.trackedBatSpeedCompetitive
    ).length

    const calculations: Calculations = {
        totalPitches: data.length,
        hits: 0,
        bip: 0,
        trackedBIP: 0,
        trackedSwingLength,
        trackedBatSpeed,
        trackedSwingLengthCompetitive,
        trackedBatSpeedCompetitive,
        ab: 0,
        pa: 0,
        hr: 0,
        so: 0,
        single: 0,
        double: 0,
        triple: 0,
        pitches: 0,
        wobaNumer: 0,
        wobaDenom: 0,
        xWobaNumer: 0,
        xWobaDenom: 0,
        runExp: 0,
        pitcherRunExp: 0,
        swings: 0,
        whiffs: 0,
        launchSpeed: 0,
        launchAngle: 0,
        releaseSpeedSum: 0,
        releaseSpinRateSum: 0,
        batSpeedSum: 0,
        batSpeedCompetitiveSum: 0,
        swingLengthSum: 0,
        swingLengthCompetitiveSum: 0,
        idealAttackAngleSum: 0,
        idealAttackAngleCompetitiveSum: 0,
        attackAngleSum: 0,
        attackAngleCompetitiveSum: 0,
        attackDirectionSum: 0,
        attackDirectionCompetitiveSum: 0,
        swingPathTiltSum: 0,
        swingPathTiltCompetitiveSum: 0,
        // Averages
        percent: null,
        avgExitVelocity: null,
        avgLaunchAngle: null,
        ba: null,
        woba: null,
        slg: null,
        slgRaw: null,
        xWoba: null,
        kPercent: null,
        swing_miss_percent: null,
        releaseSpeed: null,
        releaseSpinRate: null,
        batSpeed: null,
        batSpeedCompetitive: null,
        swingLength: null,
        swingLengthCompetitive: null,
        idealAttackAngle: null,
        idealAttackAngleCompetitive: null,
        attackAngle: null,
        attackAngleCompetitive: null,
        attackDirection: null,
        attackDirectionCompetitive: null,
        swingPathTilt: null,
        swingPathTiltCompetitive: null,
    }
    const trackedSpin = data.filter((d) => d.releaseSpinRate).length
    const trackedSpeed = data.filter((d) => d.releaseSpeed).length

    for (let i = 0; i < data.length; i++) {
        const d = data[i]

        if (!isGamefeedRawData(d.raw) && d.raw.attack_angle) {
            d.raw.ideal_angle =
                d.trackedBatSpeed &&
                d.raw.attack_angle >= 5 &&
                d.raw.attack_angle <= 20
                    ? 1
                    : 0
        }

        calculations.hits += Number(d.hits)
        calculations.ab += Number(d.ab)
        calculations.pa += Number(d.pa)
        calculations.bip += Number(d.bip)
        calculations.swings += d.is_swing ? d.is_swing : 0
        calculations.whiffs += d.is_whiff ? d.is_whiff : 0
        if (d.launchSpeed && d.launchSpeed > 0) {
            calculations.trackedBIP += 1
        }
        calculations.single += Number(d.single)
        calculations.double += Number(d.double)
        calculations.triple += Number(d.triple)
        calculations.hr += Number(d.hr)
        calculations.so += Number(d.so)
        calculations.launchSpeed += !Number.isNaN(d.launchSpeed)
            ? Number(d.launchSpeed)
            : 0
        calculations.launchAngle += !Number.isNaN(d.launchAngle)
            ? Number(d.launchAngle)
            : 0
        calculations.wobaNumer += Number(d.wobaNumer)
        calculations.wobaDenom += Number(d.wobaDenom)
        calculations.xWobaNumer += Number(d.xWobaNumer)
        calculations.xWobaDenom += Number(d.xWobaDenom)

        // pitch
        calculations.releaseSpeedSum += Number.isNaN(d.releaseSpeed)
            ? 0
            : d.releaseSpeed
        calculations.releaseSpinRateSum += Number.isNaN(d.releaseSpinRate)
            ? 0
            : d.releaseSpinRate

        if (d.deltaRunExp) {
            calculations.runExp =
                Number(calculations.runExp) +
                (Number.isNaN(d.deltaRunExp) ? 0 : d.deltaRunExp)

            calculations.pitcherRunExp =
                Number(calculations.pitcherRunExp) -
                (Number.isNaN(d.deltaRunExp) ? 0 : d.deltaRunExp)
        }

        // Add bat tracking metrics
        addBatTrackingMetricsRaw(calculations, d)

        const isPitch =
            d.event_type &&
            !['automatic_ball', 'automatic_strike', 'intent_ball'].includes(
                d.event_type
            )

        if (d.pitchType !== '' && !isPitch) {
            calculations.pitches += 1
        }
    }

    // Averages/Percents
    calculations.percent = ((calculations.pitches / totalSize) * 100).toFixed(1)

    calculations.avgExitVelocity = calculations.trackedBIP
        ? (calculations.launchSpeed / calculations.trackedBIP).toFixed(1)
        : null

    calculations.avgLaunchAngle = calculations.trackedBIP
        ? (calculations.launchAngle / calculations.trackedBIP).toFixed(0)
        : null

    calculations.ba = calculations.ab
        ? removeLeadingZero((calculations.hits / calculations.ab).toFixed(3))
        : null

    calculations.woba = calculations.wobaDenom
        ? removeLeadingZero(
              (calculations.wobaNumer / calculations.wobaDenom).toFixed(3)
          )
        : null

    if (calculations.ab) {
        const slg =
            (calculations.single +
                2 * calculations.double +
                3 * calculations.triple +
                4 * calculations.hr) /
            calculations.ab

        calculations.slgRaw = slg
        calculations.slg = threeDigitFormat(slg)
    } else {
        calculations.slg = null
    }

    if (calculations.xWobaDenom) {
        calculations.xWoba = removeLeadingZero(
            (calculations.xWobaNumer / calculations.xWobaDenom).toFixed(3)
        )
    }

    calculations.kPercent = calculations.pa
        ? ((calculations.so / calculations.pa) * 100).toFixed(1)
        : null

    calculations.swing_miss_percent = calculations.swings
        ? guff(
              (100 * calculations.whiffs) / calculations.swings,
              'swing_miss_percent'
          )
        : null

    calculations.releaseSpeed = trackedSpeed
        ? (calculations.releaseSpeedSum / trackedSpeed).toFixed(1)
        : 0

    calculations.releaseSpinRate = trackedSpin
        ? Math.round(calculations.releaseSpinRateSum / trackedSpin)
        : null

    calculations.runExp = Number(calculations.runExp).toFixed(1)
    calculations.pitcherRunExp = Number(calculations.pitcherRunExp).toFixed(1)

    addBatTrackingMetricsPercents(
        calculations,
        trackedBatSpeed,
        trackedBatSpeedCompetitive
    )

    return calculations
}

/* eslint-disable no-param-reassign */
function removeLeadingZero(value: string) {
    try {
        if (value.substring(0, 1) === '0') {
            value = value.substring(1, value.length)
        } else if (value.substring(0, 2) === '-0') {
            value = `-${value.substring(2, value.length)}`
        }
    } catch (ex) {
        /* empty */
    }

    return value
}
/* eslint-enable no-param-reassign */
